// src/pages/RestaurantOwner/Orders.jsx - WITH MANUAL BILLING AND CANCEL FEATURE

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ShoppingCart, Bell, Filter, Search, Clock, Loader, BellRing, FileText, Settings, Plus, XCircle, MessageSquare } from 'lucide-react';
import StorageService from '../../services/StorageService';
import NotificationService from '../../services/NotificationService';
import NotificationPollingService from '../../services/NotificationPollingService';
import BillingService from '../../services/BillingService';
import Receipt from '../../components/Receipt';
import BillingSettings from './BillingSettings';
import ManualBillingModal from '../../components/ManualBillingModal';
import { doc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import { useApp } from '../../context/AppContext';
import InventoryDeductionService from '../../services/InventoryDeductionService';

const OwnerOrders = ({ restaurantId }) => {
  //const { user } = useApp();
  const { user, userType } = useApp();
  console.log('🔍 Orders.jsx - Current userType:', userType); // ✅ ADD THIS
console.log('🔍 Orders.jsx - Is kitchen staff:', userType === 'kitchen'); // ✅ ADD THIS
   
  const [orders, setOrders] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedOrderForTime, setSelectedOrderForTime] = useState(null);
  const [timeInput, setTimeInput] = useState('');
  const [updating, setUpdating] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationToken, setNotificationToken] = useState(null);
  
  // BILLING STATE
  const [showReceipt, setShowReceipt] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [generatingBill, setGeneratingBill] = useState(false);
  
  // BILLING SETTINGS VIEW
  const [showBillingSettings, setShowBillingSettings] = useState(false);
  
  // MANUAL BILLING STATE
  const [showManualBilling, setShowManualBilling] = useState(false);
  const [showKOTModal, setShowKOTModal] = useState(false);
  const [menu, setMenu] = useState([]);
  const [tables, setTables] = useState([]);

  // CANCELLATION STATE
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrderForCancel, setSelectedOrderForCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  
  // ✅ NEW FILTER STATES
  const [filterTableName, setFilterTableName] = useState('All Tables'); // For Table Name filter
  const [filterDate, setFilterDate] = useState(''); // For specific calendar date (YYYY-MM-DD)
  const isKitchenStaff = userType === 'kitchen';
  
  const prevOrdersRef = useRef([]);
  const pollingIntervalRef = useRef(null);

  useEffect(() => {
    loadOrders();
    loadMenuAndTables();
    initializeNotifications();
    return cleanup;
  }, [restaurantId]);

  const cleanup = () => {
    stopPolling();
    NotificationPollingService.stopListening();
  };

  // LOAD MENU AND TABLES FOR MANUAL BILLING
  const loadMenuAndTables = async () => {
    try {
      const restaurant = await StorageService.getRestaurant(restaurantId);
      if (restaurant) {
        setMenu(restaurant.menu || []);
        const parcelOption = {
          id: 'parcel',
          name: 'Parcel/Takeaway',
          capacity: 0
        };
        
        const regularTables = restaurant.tables || [];
        setTables([parcelOption, ...regularTables]);
        // setTables(restaurant.tables || []);
      }
    } catch (error) {
      console.error('Error loading menu and tables:', error);
    }
  };

  const initializeNotifications = async () => {
    try {
      const hasPermission = Notification.permission === 'granted';
      setNotificationsEnabled(hasPermission);

      if (hasPermission) {
        const token = await NotificationService.getNotificationToken(restaurantId);
        setNotificationToken(token);
        
        NotificationService.setupForegroundListener(payload => {
          triggerNewOrderAlert();
          loadOrders();
        });

        await NotificationPollingService.ensurePermission();
        NotificationPollingService.startListening(restaurantId, (newOrder) => {
          console.log('🆕 New order callback:', newOrder);
          triggerNewOrderAlert();
          loadOrders();
        });

        console.log('✅ All notification systems initialized');
      }
    } catch (error) {
      console.error('Error initializing notifications:', error);
    }
  };

  const enableNotifications = async () => {
    const hasPermission = await NotificationPollingService.ensurePermission();
    
    if (hasPermission) {
      const token = await NotificationService.getNotificationToken(restaurantId);
      if (token) {
        setNotificationsEnabled(true);
        setNotificationToken(token);
        
        NotificationService.setupForegroundListener(payload => {
          triggerNewOrderAlert();
          loadOrders();
        });

        NotificationPollingService.startListening(restaurantId, (newOrder) => {
          triggerNewOrderAlert();
          loadOrders();
        });

        new Notification('✅ Notifications Enabled!', { 
          body: 'You will receive alerts for new orders', 
          icon: '/logo192.png' 
        });

        NotificationService.playNotificationSound();
      }
    }
  };

  const disableNotifications = async () => {
    await NotificationService.removeToken(restaurantId);
    NotificationPollingService.stopListening();
    setNotificationsEnabled(false);
    setNotificationToken(null);
  };

  const loadOrders = async () => {
    try {
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      if (restaurant) {
        const sorted = (restaurant.orders || []).sort((a, b) => {
          const dateA = new Date(a.time);
          const dateB = new Date(b.time);
          return dateB - dateA;
        });

        if (!initialLoading && prevOrdersRef.current.length > 0 && sorted.length > prevOrdersRef.current.length) {
          triggerNewOrderAlert();
        }
        
        prevOrdersRef.current = sorted;
        setOrders(sorted);
      }
    } catch (error) {
      console.error('❌ Error loading orders:', error);
    } finally {
      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  };

  const startSmartPolling = () => {
    stopPolling();
    
    const hasActiveOrders = orders.some(o => 
      o.status === 'Pending' || o.status === 'Preparing' || o.status === 'Ready'
    );
    
    const interval = hasActiveOrders ? 5000 : 15000;
    
    pollingIntervalRef.current = setInterval(loadOrders, interval);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => {
    startSmartPolling();
  }, [orders.length]);

  const triggerNewOrderAlert = () => {
    setNewOrderAlert(true);
    
    if (soundEnabled) {
      NotificationService.playNotificationSound();
    }
    
    setTimeout(() => setNewOrderAlert(false), 3000);
  };

  const openTimeModal = (order) => {
    setSelectedOrderForTime(order);
    setTimeInput(order.preparationTime || '');
    setShowTimeModal(true);
  };

  const savePreparationTime = async () => {
    if (!timeInput || timeInput <= 0) {
      alert('Please enter a valid preparation time');
      return;
    }

    try {
      setUpdating(true);
      const prepTime = parseInt(timeInput);
      
      setOrders(prevOrders => 
        prevOrders.map(o => 
          o.id === selectedOrderForTime.id 
            ? {
                ...o,
                preparationTime: prepTime,
                status: o.status === 'Pending' ? 'Preparing' : o.status,
                preparationStartTime: o.status === 'Pending' 
                  ? new Date().toISOString() 
                  : o.preparationStartTime
              }
            : o
        )
      );

      setShowTimeModal(false);
      setSelectedOrderForTime(null);
      setTimeInput('');

      const orderRef = doc(db, 'restaurants', restaurantId, 'orders', selectedOrderForTime.id);
      const updateData = {
        preparationTime: prepTime
      };

      if (selectedOrderForTime.status === 'Pending') {
        updateData.preparationStartTime = new Date().toISOString();
        updateData.status = 'Preparing';
      }

      await updateDoc(orderRef, updateData);
      StorageService.clearCache();
      
      console.log('✅ Preparation time saved');
    } catch (error) {
      console.error('❌ Error saving preparation time:', error);
      alert('Failed to save preparation time. Please try again.');
      await loadOrders();
    } finally {
      setUpdating(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const order = orders.find(o => o.id === orderId);
      
      if (newStatus === 'Preparing' && !order?.preparationTime) {
        openTimeModal(order);
        return;
      }
      
      setUpdating(true);

      setOrders(prevOrders => 
        prevOrders.map(o => {
          if (o.id !== orderId) return o;
          
          const updated = { ...o, status: newStatus };
          
          if (newStatus === 'Preparing' && !o.preparationStartTime) {
            updated.preparationStartTime = new Date().toISOString();
          }
          
          if (newStatus === 'Delivered') {
            updated.completedAt = new Date().toISOString();
          }
          
          return updated;
        })
      );

      const orderRef = doc(db, 'restaurants', restaurantId, 'orders', orderId);
      const updateData = { status: newStatus };

      if (newStatus === 'Preparing' && !order.preparationStartTime) {
        updateData.preparationStartTime = new Date().toISOString();
      }

      if (newStatus === 'Delivered') {
        updateData.completedAt = new Date().toISOString();
      }

      await updateDoc(orderRef, updateData);
      StorageService.clearCache();
      
      console.log('✅ Order status updated');
    } catch (error) {
      console.error('❌ Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
      await loadOrders();
    } finally {
      setUpdating(false);
    }
  };

  // OPEN CANCEL CONFIRMATION MODAL
  const openCancelModal = (order) => {
    setSelectedOrderForCancel(order);
    setShowCancelModal(true);
  };

  // CANCEL ORDER HANDLER
  const handleCancelOrder = async () => {
  if (!selectedOrderForCancel) return;

  try {
    setCancelling(true);
    
    // ✅ ADD THIS BLOCK BEFORE DELETION:
    try {
      console.log('🔄 Restoring inventory for cancelled order');
      const restorationResult = await InventoryDeductionService.restoreInventoryForCancelledOrder(
        restaurantId,
        selectedOrderForCancel.id
      );
      
      if (restorationResult.success) {
        console.log('✅ Inventory restored successfully');
      } else {
        console.warn('⚠️ Could not restore inventory:', restorationResult.message);
      }
    } catch (error) {
      console.error('❌ Error restoring inventory:', error);
    }
    
    const orderRef = doc(db, 'restaurants', restaurantId, 'orders', selectedOrderForCancel.id);
    
    // Delete the order from Firestore
    await deleteDoc(orderRef);
      
      // Update local state
      setOrders(prevOrders => prevOrders.filter(o => o.id !== selectedOrderForCancel.id));
      
      // Clear cache to ensure fresh data
      StorageService.clearCache();
      
      console.log('✅ Order cancelled successfully:', selectedOrderForCancel.id);
      
      // Close modal
      setShowCancelModal(false);
      setSelectedOrderForCancel(null);
      
      // Show success message
      alert(`Order #${selectedOrderForCancel.id} has been cancelled successfully!`);
      
    } catch (error) {
      console.error('❌ Error cancelling order:', error);
      alert('Failed to cancel order. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  // GENERATE BILL FOR TABLE
  const generateBillForTable = async (tableId, tableName) => {
    try {
      setGeneratingBill(true);
      
      const tableOrders = orders.filter(
        o => o.tableId === tableId && o.status === 'Delivered' && !o.billed
      );
      
      if (tableOrders.length === 0) {
        alert('No unbilled delivered orders found for this table!');
        return;
      }
      
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      const invoice = await BillingService.createInvoice(
        tableOrders,
        restaurant,
        { tableId, tableName },
        user?.username || 'Manager',
        StorageService
      );
      
      await StorageService.saveInvoice(restaurantId, invoice);
      
      if (invoice._nextInvoiceCounter) {
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        await updateDoc(restaurantRef, {
          invoiceCounter: invoice._nextInvoiceCounter
        });
      }
      
      for (const order of tableOrders) {
        const orderRef = doc(db, 'restaurants', restaurantId, 'orders', order.id);
        await updateDoc(orderRef, { 
          billed: true,
          invoiceNumber: invoice.invoiceNumber,
          billedAt: new Date().toISOString()
        });
      }
      
      setCurrentInvoice(invoice);
      setShowReceipt(true);
      
      StorageService.clearCache();
      await loadOrders();
      
      console.log('✅ Bill generated:', invoice.invoiceNumber);
    } catch (error) {
      console.error('❌ Error generating bill:', error);
      alert('Failed to generate bill. Please try again.');
    } finally {
      setGeneratingBill(false);
    }
  };

  // GENERATE MANUAL BILL
  const handleGenerateManualBill = async (orderData) => {
    try {
      setGeneratingBill(true);
      console.log('📝 Generating manual bill...', orderData);
      
      const restaurant = await StorageService.getRestaurant(restaurantId);
      
      // Create a temporary order object for billing
      const manualOrder = {
        id: `manual_${Date.now()}`,
        customerName: orderData.customerName,
        tableId: orderData.tableId,
        tableName: orderData.tableName,
        total: orderData.total,
        items: orderData.items.map(item => item.menuItemId || item.id || item.name),

itemDetails: orderData.items.map(item => ({
  name: item.name,
  price: item.price,
  quantity: item.quantity,
  category: item.category || 'Uncategorized',      // ✅ ADDED
  subcategory: item.subcategory || 'Regular', // ✅ ADDED
  portion: item.Portion || 'Full'          // ✅ ADDED
})),
        status: 'Delivered',
        time: new Date().toISOString(),
        isManualOrder: true
      };
      
      // Create invoice
      const invoice = await BillingService.createInvoice(
        [manualOrder],
        restaurant,
        { 
          tableId: orderData.tableId, 
          tableName: orderData.tableName 
        },
        user?.username || 'Manager',
        StorageService
      );
      
      // Save invoice
      await StorageService.saveInvoice(restaurantId, invoice);
      
      // Update invoice counter
      if (invoice._nextInvoiceCounter) {
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        await updateDoc(restaurantRef, {
          invoiceCounter: invoice._nextInvoiceCounter
        });
      }

      // FIXED: Use setDoc to CREATE new manual order (not updateDoc)
      const orderRef = doc(db, 'restaurants', restaurantId, 'orders', manualOrder.id);
      await setDoc(orderRef, {
        ...manualOrder,
        billed: true,
        invoiceNumber: invoice.invoiceNumber,
        billedAt: new Date().toISOString()
      });
      
      // Show receipt
      setCurrentInvoice(invoice);
      setShowReceipt(true);
      
      StorageService.clearCache();
      await loadOrders();
      
      console.log('✅ Manual bill generated:', invoice.invoiceNumber);
      alert('✅ Manual bill generated successfully!');
    } catch (error) {
      console.error('❌ Error generating manual bill:', error);
      alert('Failed to generate manual bill. Please try again.');
    } finally {
      setGeneratingBill(false);
    }
  };
// CREATE KOT (Kitchen Order Ticket)
// CREATE KOT (Kitchen Order Ticket)
const handleCreateKOT = async (kotData) => {
  try {
    setGeneratingBill(true);
    console.log('📋 Creating KOT...', kotData);
    
    const restaurant = await StorageService.getRestaurant(restaurantId);
    
    // ✅ Create items array for Firestore security rules
    const itemsArray = kotData.items.map(item => item.menuItemId || item.id);
    
    // Create KOT order object
    const kotOrder = {
      id: `kot_${Date.now()}`,
customerName: kotData.customerName || kotData.tableName || 'Dine-in Customer',      tableId: kotData.tableId,
      tableName: kotData.tableName,
      total: kotData.total,
      items: itemsArray, // ✅ Required by security rules
      itemDetails: kotData.items.map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        category: item.category || 'Uncategorized',
        subcategory: item.subcategory || 'Regular',
        portion: item.portion || 'Full'
      })),
      specialInstructions: kotData.specialInstructions || '',
      status: 'Pending',
      time: new Date().toISOString(),
      isKOT: true,
      billed: false
    };
    
    // Save KOT order to Firestore
    const orderRef = doc(db, 'restaurants', restaurantId, 'orders', kotOrder.id);
    await setDoc(orderRef, kotOrder);
    
    StorageService.clearCache();
    await loadOrders();
    
    console.log('✅ KOT created:', kotOrder.id);
    alert('✅ KOT created successfully! Kitchen will be notified.');
  } catch (error) {
    console.error('❌ Error creating KOT:', error);
    alert('Failed to create KOT. Please try again.');
  } finally {
    setGeneratingBill(false);
  }
};
  // GET TABLES WITH UNBILLED ORDERS
  const getTablesWithUnbilledOrders = useMemo(() => {
    const tablesMap = new Map();
    
    orders.forEach(order => {
      if (order.status === 'Delivered' && !order.billed && order.tableId) {
        if (!tablesMap.has(order.tableId)) {
          tablesMap.set(order.tableId, {
            tableId: order.tableId,
            tableName: order.tableName,
            orderCount: 0,
            totalAmount: 0
          });
        }
        
        const tableData = tablesMap.get(order.tableId);
        tableData.orderCount++;
        tableData.totalAmount += order.total;
      }
    });
    
    return Array.from(tablesMap.values());
  }, [orders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Preparing': return 'bg-blue-100 text-blue-800';
      case 'Ready': return 'bg-green-100 text-green-800';
      case 'Delivered': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchStatus = filterStatus === 'All' || order.status === filterStatus;
      const matchSearch =
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.tableName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.id.toString().includes(searchTerm);
      
      // ✅ TABLE NAME FILTER LOGIC
      const matchTableName = filterTableName === 'All Tables' || order.tableName === filterTableName;

      // ✅ DATE FILTER LOGIC
      let matchDate = true;
      
      if (filterDate) {
        // order.time is ISO string. Convert it to YYYY-MM-DD string for direct comparison with filterDate
        const orderDateString = new Date(order.time).toISOString().split('T')[0];
        matchDate = orderDateString === filterDate;
      }

      return matchStatus && matchSearch && matchTableName && matchDate;
    });
  }, [orders, filterStatus, searchTerm, filterTableName, filterDate]); // ✅ Update dependencies

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleFilterChange = useCallback((e) => {
    setFilterStatus(e.target.value);
  }, []);
  
  // ✅ NEW HANDLERS
  const handleTableNameFilterChange = useCallback((e) => {
    setFilterTableName(e.target.value);
  }, []);

  const handleDateFilterChange = useCallback((e) => {
    setFilterDate(e.target.value);
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => !prev);
  }, []);

  // IF BILLING SETTINGS IS OPEN, SHOW THAT INSTEAD
  if (showBillingSettings) {
    return (
      <BillingSettings 
        restaurantId={restaurantId}
        onBack={() => setShowBillingSettings(false)}
      />
    );
  }

  if (initialLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Orders</h1>
        <div className="relative flex flex-wrap items-center gap-2 md:gap-3">
         {!isKitchenStaff && (
    <>
      {/* MANUAL BILLING BUTTON */}
          <button
            onClick={() => setShowManualBilling(true)}
            className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-lg transition bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 font-semibold shadow-md"
            title="Create Manual Bill"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Manual Bill</span>
            <span className="sm:hidden">Bill</span>
          </button>
{/* KOT BUTTON */}
          <button
            onClick={() => setShowKOTModal(true)}
            className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-lg transition bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-md"
            title="Create Kitchen Order Ticket"
          >
            <FileText className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Create KOT</span>
            <span className="sm:hidden">KOT</span>
          </button>

          <button
            onClick={() => setShowBillingSettings(true)}
            className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-lg transition bg-purple-100 text-purple-600 hover:bg-purple-200 border border-purple-300"
            title="Billing Settings"
          >
            <Settings className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Billing Settings</span>
            <span className="sm:hidden">Settings</span>
          </button>
</>
  )}
          <button
            onClick={notificationsEnabled ? disableNotifications : enableNotifications}
            className={`flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-lg transition ${
              notificationsEnabled
                ? 'bg-green-100 text-green-600 border border-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <BellRing className={`w-4 h-4 md:w-5 md:h-5 ${notificationsEnabled ? 'text-green-500 animate-pulse' : 'text-gray-500'}`} />
            <span className="hidden sm:inline">{notificationsEnabled ? 'Push ON' : 'Push OFF'}</span>
            <span className="sm:hidden">{notificationsEnabled ? 'ON' : 'OFF'}</span>
          </button>

          <button
            onClick={toggleSound}
            className={`flex items-center gap-2 text-xs md:text-sm px-3 py-2 rounded-lg transition ${
              soundEnabled
                ? 'bg-orange-100 text-orange-600 border border-orange-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Bell className={`w-4 h-4 md:w-5 md:h-5 ${soundEnabled ? 'text-orange-500' : 'text-gray-500'}`} />
            <span className="hidden sm:inline">{soundEnabled ? 'Sound On' : 'Sound Off'}</span>
            <span className="sm:hidden">{soundEnabled ? '🔊' : '🔇'}</span>
          </button>
          
          {newOrderAlert && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
          )}
        </div>
      </div>

      {!notificationsEnabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-xs md:text-sm text-blue-800 flex items-start gap-2">
            <BellRing className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Enable Push Notifications</strong> to receive order alerts even when your browser is closed or in the background!
            </span>
          </p>
        </div>
      )}

      {/* BILLING SECTION */}
{!isKitchenStaff && getTablesWithUnbilledOrders.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-xl p-6 mb-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-800">Ready for Billing</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Tables with delivered orders ready to generate bills
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getTablesWithUnbilledOrders.map(table => (
              <div key={table.tableId} className="bg-white rounded-lg p-4 border-2 border-green-200 hover:border-green-400 transition">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-800">{table.tableName}</h3>
                    <p className="text-sm text-gray-600">{table.orderCount} order(s)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">₹{table.totalAmount}</p>
                  </div>
                </div>
                <button
                  onClick={() => generateBillForTable(table.tableId, table.tableName)}
                  disabled={generatingBill}
                  className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 disabled:opacity-50 transition font-semibold flex items-center justify-center gap-2"
                >
                  {generatingBill ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Generate Bill
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 bg-white shadow-sm p-4 rounded-xl mb-6">
        <div className="flex items-center gap-2 border px-3 py-2 rounded-lg flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search..."
            className="outline-none text-sm w-full"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <div className="flex items-center gap-2 border px-3 py-2 rounded-lg">
          <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <select
            className="text-sm outline-none"
            value={filterStatus}
            onChange={handleFilterChange}
          >
            <option>All</option>
            <option>Pending</option>
            <option>Preparing</option>
            <option>Ready</option>
            <option>Delivered</option>
          </select>
        </div>
        
        {/* ✅ NEW: TABLE NAME FILTER */}
        <div className="flex items-center gap-2 border px-3 py-2 rounded-lg">
          <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <select
            className="text-sm outline-none"
            value={filterTableName}
            onChange={handleTableNameFilterChange}
          >
            <option>All Tables</option>
            {tables.map((table) => (
              <option key={table.id} value={table.name}>{table.name}</option>
            ))}
          </select>
        </div>

        {/* ✅ NEW: DATE FILTER (Calendar Input) */}
        <div className="flex items-center gap-2 border px-3 py-2 rounded-lg">
          <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <input
            type="date" // ⬅️ Changed to date input
            className="text-sm outline-none bg-white"
            value={filterDate}
            onChange={handleDateFilterChange}
            title="Filter by specific date"
          />
          {/* Button to clear the date filter */}
          {filterDate && (
            <button 
              onClick={() => setFilterDate('')}
              className="text-red-500 hover:text-red-700 ml-1"
              title="Clear date filter"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <button
          onClick={loadOrders}
          disabled={updating}
          className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 transition whitespace-nowrap"
        >
          Refresh
        </button>
      </div>

      {filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div
              key={order.id}
              className="bg-white rounded-xl shadow-md p-4 md:p-6 hover:shadow-lg transition"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base md:text-lg font-bold text-gray-800">Order #{order.id}</h3>
                    {order.billed && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                        BILLED
                      </span>
                    )}
                    {order.isManualOrder && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-semibold">
                        MANUAL
                      </span>
                    )}
{order.isKOT && (
  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
    KOT
  </span>
)}
                  </div>
                  <p className="text-gray-600 text-xs md:text-sm">{new Date(order.time).toLocaleString()}</p>
                  {order.customerName && (
                    <p className="text-gray-600 text-xs md:text-sm">Customer: {order.customerName}</p>
                  )}
                  {order.tableName && (
                    <p className="text-blue-600 text-xs md:text-sm font-semibold flex items-center gap-1">
                      📍 {order.tableName}
                    </p>
                  )}
                  {order.preparationTime && (
                    <p className="text-orange-600 text-xs md:text-sm font-semibold flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 md:w-4 md:h-4" /> Prep Time: {order.preparationTime} mins
                    </p>
                  )}
                  {order.invoiceNumber && (
                    <p className="text-green-600 text-xs md:text-sm font-semibold mt-1">
                      Invoice: {order.invoiceNumber}
                    </p>
                  )}
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xl md:text-2xl font-bold text-orange-500">₹{order.total}</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>

              <div className="border-t pt-4 mb-4">
                <h4 className="font-semibold text-gray-700 mb-2 text-sm md:text-base">Items:</h4>
                <ul className="space-y-1">
                  {order.itemDetails?.map((item, idx) => (
                    <li key={idx} className="text-gray-600 flex justify-between text-xs md:text-sm">
                      <span>{item.name} × {item.quantity}</span>
                      <span>₹{item.price * item.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ✅ SPECIAL INSTRUCTIONS DISPLAY */}
              {order.specialInstructions && order.specialInstructions.trim() && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-800 mb-1 text-sm md:text-base">Special Instructions</h4>
                      <p className="text-yellow-700 text-xs md:text-sm italic">
                        "{order.specialInstructions}"
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ACTION BUTTONS WITH CANCEL - Only show for non-delivered and non-manual orders */}
              {order.status !== 'Delivered' && !order.isManualOrder && (
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* PENDING STATUS BUTTONS */}
                  {order.status === 'Pending' && (
                    <>
                      <button
                        onClick={() => openTimeModal(order)}
                        disabled={updating || cancelling}
                        className="flex-1 bg-blue-500 text-white py-2 px-3 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 disabled:opacity-50 transition text-sm md:text-base"
                      >
                        <Clock className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="hidden sm:inline">Accept & Set Time</span>
                        <span className="sm:hidden">Accept</span>
                      </button>
                      <button
                        onClick={() => openCancelModal(order)}
                        disabled={updating || cancelling}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition flex items-center justify-center gap-2 text-sm md:text-base"
                      >
                        <XCircle className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="hidden sm:inline">Cancel</span>
                        <span className="sm:hidden">Cancel</span>
                      </button>
                    </>
                  )}
                  
                  {/* PREPARING STATUS BUTTONS */}
                  {order.status === 'Preparing' && (
                    <>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'Ready')}
                        disabled={updating || cancelling}
                        className="flex-1 bg-green-500 text-white py-2 px-3 rounded-lg hover:bg-green-600 disabled:opacity-50 transition text-sm md:text-base"
                      >
                        Mark as Ready
                      </button>
                      <button
                        onClick={() => openTimeModal(order)}
                        disabled={updating || cancelling}
                        className="px-3 md:px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                        title="Edit Preparation Time"
                      >
                        <Clock className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                      <button
                        onClick={() => openCancelModal(order)}
                        disabled={updating || cancelling}
                        className="px-3 md:px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition"
                        title="Cancel Order"
                      >
                        <XCircle className="w-4 h-4 md:w-5 md:h-5" />
                      </button>
                    </>
                  )}

                  {/* READY STATUS BUTTON - No cancel button here */}
                 {/* READY STATUS BUTTON - Kitchen staff cannot mark delivered */}
{order.status === 'Ready' && !isKitchenStaff && (
  <button
    onClick={() => updateOrderStatus(order.id, 'Delivered')}
    disabled={updating}
    className="flex-1 bg-gray-500 text-white py-2 px-3 rounded-lg hover:bg-gray-600 disabled:opacity-50 transition text-sm md:text-base"
  >
    Mark as Delivered
  </button>
)}

{/* Show message for kitchen staff */}
{order.status === 'Ready' && isKitchenStaff && (
  <div className="flex-1 bg-blue-50 border-2 border-blue-200 rounded-lg p-3 text-center">
    <p className="text-blue-700 font-semibold text-sm">
      ✅ Order Ready - Waiting for manager to deliver
    </p>
  </div>
)}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <ShoppingCart className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 opacity-50" />
          <p className="text-sm md:text-base">No orders yet</p>
        </div>
      )}

      {/* CANCEL ORDER CONFIRMATION MODAL */}
      {showCancelModal && selectedOrderForCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 md:p-8 w-full max-w-md">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2 text-center">Cancel Order</h2>
            <p className="text-gray-600 mb-6 text-center text-sm md:text-base">
              Are you sure you want to cancel Order #{selectedOrderForCancel.id}?
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800 flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <span>This action cannot be undone. The order will be permanently removed from the system.</span>
              </p>
            </div>

            <div className="space-y-2 mb-6 text-sm text-gray-700">
              <p><strong>Customer:</strong> {selectedOrderForCancel.customerName}</p>
              <p><strong>Table:</strong> {selectedOrderForCancel.tableName}</p>
              <p><strong>Amount:</strong> ₹{selectedOrderForCancel.total}</p>
              <p><strong>Status:</strong> {selectedOrderForCancel.status}</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedOrderForCancel(null);
                }}
                disabled={cancelling}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition text-sm md:text-base font-semibold"
              >
                No, Keep Order
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={cancelling}
                className="flex-1 bg-red-500 text-white py-3 rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2 transition text-sm md:text-base font-semibold"
              >
                {cancelling ? (
                  <>
                    <Loader className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                    <span className="hidden sm:inline">Cancelling...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 md:w-5 md:h-5" />
                    Yes, Cancel Order
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preparation Time Modal */}
      {showTimeModal && selectedOrderForTime && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 md:p-8 w-full max-w-md">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Set Preparation Time</h2>
            <p className="text-gray-600 mb-6 text-sm md:text-base">Order #{selectedOrderForTime.id}</p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preparation Time (minutes)
              </label>
              <input
                type="number"
                min="1"
                max="180"
                value={timeInput}
                onChange={(e) => setTimeInput(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-base"
                placeholder="e.g., 15"
                autoFocus
                disabled={updating}
              />
              <p className="text-xs text-gray-500 mt-2">
                This time will be shown to the customer for tracking their order
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTimeModal(false);
                  setSelectedOrderForTime(null);
                  setTimeInput('');
                }}
                disabled={updating}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition text-sm md:text-base"
              >
                Cancel
              </button>
              <button
                onClick={savePreparationTime}
                disabled={updating}
                className="flex-1 bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 transition text-sm md:text-base"
              >
                {updating ? (
                  <>
                    <Loader className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  selectedOrderForTime.status === 'Pending' ? 'Accept & Start' : 'Update Time'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MANUAL BILLING MODAL */}
{!isKitchenStaff && showManualBilling && (
          <ManualBillingModal
          restaurantId={restaurantId}
          menu={menu}
          tables={tables}
          onClose={() => setShowManualBilling(false)}
          onGenerateBill={handleGenerateManualBill}
        />
      )}
{/* KOT MODAL */}
      {!isKitchenStaff && showKOTModal && (
        <ManualBillingModal
          restaurantId={restaurantId}
          menu={menu}
          tables={tables}
          onClose={() => setShowKOTModal(false)}
          onGenerateBill={handleCreateKOT}
          isKOT={true}
        />
      )}
      {/* RECEIPT MODAL */}
     {!isKitchenStaff && showReceipt && currentInvoice && (
        <Receipt
          invoice={currentInvoice}
          onClose={() => {
            setShowReceipt(false);
            setCurrentInvoice(null);
          }}
          onPrint={async () => {
            try {
              await StorageService.markInvoiceAsPrinted(restaurantId, currentInvoice.invoiceNumber);
              console.log('✅ Invoice marked as printed');
            } catch (error) {
              console.error('❌ Error marking invoice as printed:', error);
            }
          }}
        />
      )}
    </div>
  );
};

export default OwnerOrders;